<script lang="ts" setup>
import { TransitionChild, TransitionRoot } from '@headlessui/vue'
import { trackClick, trackModal } from '@shared/tracking'
import { whenever } from '@vueuse/core'
import Header from '@consumer/modules/v2/layout/Header.vue'
import { fadeInOut, modalDropdown } from '@consumer/utils/transitions'

defineOptions({
  inheritAttrs: false,
})

withDefaults(defineProps<{
  open: boolean
  name: string
  hideCloseButton?: boolean
  disableClose?: boolean
  pageTitle?: {
    href?: string
    text: string
  }
}>(), {  })

const emit = defineEmits(['close', 'remove'])

const onClickClose = (e: Event) => {
  emit('close')
}

whenever(() => __props.open, () => {
  trackModal(__props.name)
})

const slots: any = useSlots()

const headerRef = ref<InstanceType<typeof Header>>()

const afterLeave = () => {
  emit('remove')
}
</script>

<template>
  <TransitionRoot as="template" :show="open" :unmount="false" @afterLeave="afterLeave">
    <div class="absolute inset-0">
      <TransitionChild as="template" v-bind="fadeInOut">
        <div class="fixed inset-0 w-full h-full bg-overlay bg-opacity-40 transition-opacity cursor-not-allowed"/>
      </TransitionChild>
      <TransitionChild as="template" v-bind="modalDropdown">
        <dialog
          class="fixed inset-0 w-full h-full flex justify-center items-start z-dialog p-0 v2-modal-fullscreen"
          :static="true"
          :initialFocus="headerRef?.closeButtonEl"
        >
          <div class="relative w-full h-full">
            <div
              class="
                relative max-h-full mx-auto
                bg-white
                fixed inset-0 w-full h-full
                flex flex-col justify-evenly
                bg-no-repeat bg-contain
                v2-modal-fullscreen-panel
              "
              v-bind="$attrs"
              aria-hidden="false"
              aria-modal="true"
              @click="trackClick"
            >
              <slot name="header">
                <Header type="modal" :disableClose="disableClose" @close="onClickClose">
                  <h1 class="text-sm leading-none font-medium text-v2-tertiary">
                    <component :is="pageTitle?.href ? 'a' : 'div'" :href="pageTitle?.href">
                      {{ pageTitle?.text }}
                    </component>
                  </h1>
                </Header>
              </slot>

              <div class="flex-auto flex flex-col overflow-y-auto v2-modal-fullscreen-content">
                <div :id="`${name}-content`" class="h-full v2-modal-fullscreen-content-wrapper">
                  <!-- Main content -->
                  <slot/>
                </div>
              </div>

              <div
                v-if="slots.actions"
                class="w-full flex justify-center bg-white <md:rounded-t-v2lg
              px-8 py-4 fixed bottom-0 z-5 shadow-v2lg"
              >
                <slot name="actions"/>

              </div>
            </div>

            <!-- This element is to trick the browser to not focus any focusable element inside the modal -->
            <button class="opacity-0 absolute bottom-0"/>
          </div>
        </dialog>
      </TransitionChild>
    </div>
  </TransitionRoot>
</template>
